/**
 * Firebase クライアント初期化（コア）
 * #2133: darshana の src/lib/firebase/client.ts と同じ構成。
 * ここはインスタンス初期化のみを担い、ドメイン API は持たない。
 */
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { logger } from '../logger';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

/** 設計 §3: callable は asia-northeast1 を指定して呼ぶ（既定のリージョンでは見つからない） */
export const FUNCTIONS_REGION = import.meta.env.VITE_FUNCTIONS_REGION || 'asia-northeast1';

// 環境変数が設定されているかチェック
export const isConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let functions: Functions | null = null;

if (isConfigured) {
  try {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  } catch (error) {
    logger.error('Firebase initialization error', error);
  }
}

if (app) {
  try {
    auth = getAuth(app);
    if (useEmulators) {
      connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
      logger.info('Connected to Auth Emulator');
    }
  } catch (error) {
    logger.error('Firebase Auth initialization error', error);
  }
}

if (app) {
  try {
    db = getFirestore(app);
    if (useEmulators) {
      connectFirestoreEmulator(db, '127.0.0.1', 8080);
      logger.info('Connected to Firestore Emulator');
    }
  } catch (error) {
    logger.error('Firebase Firestore initialization error', error);
  }
}

if (app) {
  try {
    storage = getStorage(app);
    if (useEmulators) {
      connectStorageEmulator(storage, '127.0.0.1', 9199);
      logger.info('Connected to Storage Emulator');
    }
  } catch (error) {
    logger.error('Firebase Storage initialization error', error);
  }
}

if (app) {
  try {
    functions = getFunctions(app, FUNCTIONS_REGION);
    if (useEmulators) {
      connectFunctionsEmulator(functions, '127.0.0.1', 5001);
      logger.info('Connected to Functions Emulator');
    }
  } catch (error) {
    logger.error('Firebase Functions initialization error', error);
  }
}

export { app, auth, db, storage, functions };
